import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Navigate, useNavigate } from "react-router";
import { PublicLayout } from "../components/PanelLayout";
import { Alert, Button, Card, CopyButton, Loading, TextInput } from "../components/ui";
import { api, errorMessage } from "../lib/api";
import { homeFor, useMe } from "../lib/auth";

/** Configuração do aplicativo autenticador (obrigatório para admin). */
export function TotpSetup() {
  const { data: me, isLoading } = useMe();
  const [setup, setSetup] = useState<{ qrDataUrl: string; secret: string } | null>(null);
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();

  if (isLoading) return <Loading />;
  if (!me) return <Navigate to="/entrar" replace />;

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      setSetup(await api.post("/v1/auth/totp/setup"));
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ recoveryCodes: string[] }>("/v1/auth/totp/confirm", { code });
      setRecovery(r.recoveryCodes);
      await qc.invalidateQueries({ queryKey: ["me"] });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicLayout>
      <Card>
        <h1>Aplicativo autenticador</h1>
        <p className="muted" style={{ margin: "12px 0 16px" }}>
          Para a sua função, a entrada pede também um código do aplicativo autenticador (Google
          Authenticator, Microsoft Authenticator ou similar).
        </p>
        {error ? <Alert kind="risk">{error}</Alert> : null}
        {recovery ? (
          <div className="stack">
            <Alert kind="warning" title="Guarde estes códigos de recuperação">
              Cada um serve uma vez, se você perder o celular. Eles não aparecem de novo.
            </Alert>
            <div className="copy-box">{recovery.join("  ")}</div>
            <CopyButton text={recovery.join("\n")} label="Copiar códigos" />
            <Button block onClick={() => navigate(homeFor(me), { replace: true })}>
              Continuar
            </Button>
          </div>
        ) : !setup ? (
          <Button block loading={busy} onClick={start}>
            Configurar agora
          </Button>
        ) : (
          <form className="stack" onSubmit={confirm}>
            <img src={setup.qrDataUrl} alt="QR Code do aplicativo autenticador" className="qr" />
            <p className="small muted">Sem câmera? Digite a chave no aplicativo:</p>
            <div className="copy-box">{setup.secret}</div>
            <TextInput
              label="Código de 6 dígitos do aplicativo"
              inputMode="numeric"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            />
            <Button type="submit" block loading={busy}>
              Confirmar
            </Button>
          </form>
        )}
      </Card>
    </PublicLayout>
  );
}
